/**
 * Protected layout — sidebar + top bar + scrolling main pane.
 * Wraps every authenticated route.
 */
import { useState } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { IamUserDetail } from "@dfp/api-client";
import { useAuth } from "../auth/AuthContext.js";
import { cn } from "../lib/utils.js";
import { api } from "../lib/api.js";

interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Optional permission required to see the item (omit = always visible). */
  readonly requires?: string;
}

interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * Sidebar grouped by what the operator is DOING, not by aggregate
 * name. Five buckets, ordered by how often a daily operator touches
 * them:
 *
 *   Overview   the home-screen view + the cross-fleet roll-up.
 *   Lending    everything tied to a single loan's lifecycle —
 *              customers, loans, the contracts they sign, payments
 *              against them. Dealers live here too: they're the
 *              originators of the lending pipeline.
 *   Devices    the physical layer — device list + the price catalog
 *              that drives loan terms. Fleet health is intentionally
 *              up in Overview, not here — operators jump there for
 *              one-glance triage, not as part of a per-device dive.
 *   Compliance audit + approvals — read-only quorum-checked surfaces.
 *   Admin      platform-config-level: IAM, settings, DLQ.
 *
 * Each NavItem keeps its own `requires`; an entire group is hidden
 * when every item in it is filtered out, so a dealer-scoped operator
 * never sees an empty "Admin" header.
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard" },
      { to: "/fleet", label: "Fleet health", requires: "device.read" },
      { to: "/reports", label: "Reports", requires: "report.read" },
      { to: "/accounting/cash", label: "Cash & Accounts", requires: "ledger.read" },
      { to: "/accounting/expenses", label: "Expenses", requires: "ledger.read" },
      { to: "/accounting/stock", label: "Stock", requires: "ledger.read" },
    ],
  },
  {
    label: "Lending",
    items: [
      // Internal route name stays /loans (loan_id is the domain
      // identifier, the URL stays stable for bookmarks) but the
      // operator-facing label is "Financing" — that's what the
      // product team + dealer-floor agents call the offering.
      { to: "/loans", label: "Financing", requires: "loan.read" },
      { to: "/customers", label: "Customers", requires: "customer.read" },
      { to: "/dealers", label: "Dealers", requires: "dealer.read" },
      { to: "/payments", label: "Payments", requires: "payment.read" },
      { to: "/contracts", label: "Contracts", requires: "contract.read" },
    ],
  },
  {
    label: "Devices",
    items: [
      { to: "/devices", label: "Devices", requires: "device.read" },
      { to: "/model-prices", label: "Model Prices", requires: "device.read" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/audit", label: "Audit", requires: "audit.read" },
      { to: "/approvals", label: "Approvals", requires: "iam.approval.approve" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/iam/users", label: "Users", requires: "iam.user.read" },
      { to: "/iam/roles", label: "Roles & permissions", requires: "iam.user.read" },
      { to: "/admin/outbox", label: "Outbox DLQ", requires: "outbox.replay" },
      { to: "/admin/settings", label: "Settings", requires: "platform.settings.read" },
      { to: "/admin/payment-sandbox", label: "Payment sandbox", requires: "payment.sandbox" },
    ],
  },
];

const ICONS: Record<string, (className: string) => JSX.Element> = {
  "/": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z"
      />
    </svg>
  ),
  "/loans": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 8h6m-6 4h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  "/customers": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
  "/dealers": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  "/devices": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  ),
  /*
    Fleet health — heart-pulse / ECG glyph. The dashboard is a
    triage / health-check view, not a per-device drill-down, so the
    iconography intentionally diverges from "/devices" (which uses
    the phone outline). Heroicons-style stroke @ 2.5 so it matches
    every other glyph in this map.
  */
  "/fleet": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h3l3-9 6 18 3-9h3" />
    </svg>
  ),
  /*
    Reports — chart/trend glyph. Distinct from the dashboard icon
    (squares grid) so the operator can tell "where I'm reading raw
    health" from "where I'm reading aggregated finance".
  */
  "/reports": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 14l4-4 4 4 5-5" />
    </svg>
  ),
  "/model-prices": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 7h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zM9 16h6M9 12h6M9 8h6"
      />
    </svg>
  ),
  "/payments": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  ),
  "/contracts": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  ),
  "/audit": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  "/approvals": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
  "/iam/users": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  ),
  "/iam/roles": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
  "/admin/outbox": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  "/admin/settings": (className) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <circle cx={12} cy={12} r={3} />
    </svg>
  ),
};

export function AppLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState(false);

  // Fetch logged in user detail to get displayName and email
  const detail = useQuery({
    queryKey: ["iam", "users", "detail", user?.sub],
    queryFn: () => api.get<IamUserDetail>(`/api/iam/users/${user!.sub}`),
    enabled: !!user?.sub,
  });

  const displayName =
    detail.data?.user.displayName ?? (user ? `Operator ${user.sub.slice(0, 8)}…` : "Operator");
  const email = detail.data?.user.email ?? "";

  // Initials logic for user profile avatar
  const initials = detail.data?.user.displayName
    ? detail.data.user.displayName.slice(0, 2).toUpperCase()
    : user
      ? user.sub.slice(0, 2).toUpperCase()
      : "OP";

  return (
    <div className="flex h-full bg-simuflex-mist font-sans overflow-hidden">
      {/* Sidebar: Styled deep midnight navy ground (#122A4D) with collapsible width */}
      <aside
        className={cn(
          // select-none: the nav chrome stays non-selectable (kept from the
          // old shell-wide rule); page content is now selectable/copyable.
          "shrink-0 flex flex-col bg-simuflex-deepNavy text-slate-300 border-r border-white/5 relative z-20 shadow-[8px_0_32px_rgba(9,15,30,0.2)] transition-all duration-300 select-none",
          collapsed ? "w-20" : "w-64",
        )}
      >
        <div
          className={cn(
            "py-5 border-b border-white/5 flex",
            collapsed
              ? "flex-col items-center space-y-4 px-2"
              : "px-5 items-center justify-between",
          )}
        >
          {collapsed ? (
            <>
              {/* Sidebar Collapse Toggle Button */}
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="p-1.5 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-all flex items-center justify-center shrink-0 animate-fade-in"
                title="Expand Sidebar"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18" />
                  <path d="m14 15 3-3-3-3" />
                </svg>
              </button>

              {/* Logo */}
              <svg
                className="w-9 h-9 shrink-0 drop-shadow-[0_2px_8px_rgba(29,185,84,0.25)] animate-fade-in"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="34"
                  y="16"
                  width="32"
                  height="68"
                  rx="6"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="4"
                />
                <rect x="46" y="20" width="8" height="2" rx="1" fill="#ffffff" opacity="0.3" />
                <circle cx="50" cy="76" r="3" fill="#ffffff" opacity="0.5" />
                <path
                  d="M21 61C21 61 24 74 36 74C48 74 53 58 51 46C49 34 40 33 42 42C44 51 62 36 76 22C76 22 64 34 54 34C44 34 42 24 52 20C62 16 70 30 58 50C46 70 32 68 21 61Z"
                  fill="#1DB954"
                />
              </svg>
            </>
          ) : (
            <>
              {/* Brand logo + Wordmark on the left */}
              <div className="flex items-center space-x-3 min-w-0 animate-fade-in flex-1">
                <svg
                  className="w-9 h-9 shrink-0 drop-shadow-[0_2px_8px_rgba(29,185,84,0.25)]"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="34"
                    y="16"
                    width="32"
                    height="68"
                    rx="6"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="4"
                  />
                  <rect x="46" y="20" width="8" height="2" rx="1" fill="#ffffff" opacity="0.3" />
                  <circle cx="50" cy="76" r="3" fill="#ffffff" opacity="0.5" />
                  <path
                    d="M21 61C21 61 24 74 36 74C48 74 53 58 51 46C49 34 40 33 42 42C44 51 62 36 76 22C76 22 64 34 54 34C44 34 42 24 52 20C62 16 70 30 58 50C46 70 32 68 21 61Z"
                    fill="#1DB954"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-extrabold text-white leading-none tracking-tight flex items-center">
                    <span>Simu</span>
                    <span className="text-simuflex-green">Flex</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5 truncate">
                    Admin Console
                  </div>
                </div>
              </div>

              {/* Sidebar Collapse Toggle Button on the right */}
              <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="p-1.5 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-all flex items-center justify-center shrink-0 animate-fade-in"
                title="Collapse Sidebar"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18" />
                  <path d="m15 15-3-3 3-3" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Navigation panel */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4 scrollbar-thin">
          {NAV_GROUPS.map((group) => {
            // Items the current user is permitted to see. An entire
            // group disappears if everything in it gets filtered
            // (e.g. a dealer-scoped operator never sees an "Admin"
            // header sitting above an empty list).
            const visibleItems = group.items.filter(
              (item) => !item.requires || !user || user.permissions.includes(item.requires),
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.label} className="space-y-1">
                {/*
                  Group label — hidden when the sidebar collapses to
                  the icon-only width, since the user can no longer
                  read the surrounding context anyway. A 1px divider
                  separates groups in collapsed mode so the rhythm
                  doesn't collapse to one undifferentiated stripe.
                */}
                {collapsed ? (
                  <div className="mx-2 h-px bg-white/5" />
                ) : (
                  <div className="px-4 pt-1 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500/80">
                    {group.label}
                  </div>
                )}
                {visibleItems.map((item) => {
                  const active = path === item.to || (item.to !== "/" && path.startsWith(item.to));
                  const IconComponent = ICONS[item.to];
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "flex items-center rounded-xl transition-all duration-150 border-l-2",
                        collapsed
                          ? "justify-center px-2 py-3"
                          : "space-x-3 px-4 py-2.5 text-[13px] font-semibold tracking-wide",
                        active
                          ? "bg-white/[0.04] text-simuflex-green border-l-simuflex-green shadow-inner"
                          : "text-slate-400 hover:bg-white/[0.02] hover:text-slate-200 border-l-transparent",
                      )}
                    >
                      {IconComponent ? IconComponent("w-4 h-4 shrink-0 transition-colors") : null}
                      {!collapsed && <span className="animate-fade-in">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer Slogan info */}
        {!collapsed && (
          <div className="px-6 py-4 border-t border-white/5 bg-black/10 flex items-center justify-between text-[10px] text-slate-500 font-semibold tracking-widest uppercase animate-fade-in">
            <span>TZ - Tanzania</span>
            <span className="w-1.5 h-1.5 rounded-full bg-simuflex-green animate-ping" />
          </div>
        )}
      </aside>

      {/* Main Workspace container */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative z-10">
        {/* Top Header: Glassmorphism layout */}
        <header className="h-16 shrink-0 border-b border-simuflex-line bg-white/70 backdrop-blur-md px-8 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3 text-xs text-simuflex-slate font-medium">
            {/* API Health indicator */}
            <span className="w-2.5 h-2.5 rounded-full bg-simuflex-green animate-pulse-glow" />
            <span>Operational Mode: Production</span>
          </div>

          {/* User profile dropdown container wrapping a Link to the user profile details */}
          <div className="flex items-center space-x-4">
            <Link
              to="/iam/users/$id"
              params={{ id: user?.sub ?? "" }}
              disabled={!user?.sub}
              className="flex items-center space-x-3 text-right hover:opacity-80 transition-opacity"
              title="View my Profile details"
            >
              <div>
                <div className="text-xs font-bold text-simuflex-navy">{displayName}</div>
                <div className="text-[10px] font-semibold text-simuflex-slate mt-0.5 truncate max-w-[150px]">
                  {email || (user?.userType ?? "Staff")}
                </div>
              </div>
              <div className="w-9 h-9 rounded-full bg-simuflex-green/10 text-simuflex-forestGreen flex items-center justify-center font-bold text-sm border border-simuflex-green/20">
                {initials}
              </div>
            </Link>

            {/*
              Self-service password change moved OUT of the header (it cluttered
              the top bar). It now lives on the user's own profile page
              (UserDetailPage) — reachable by clicking the name/avatar above.
            */}
            <div className="h-5 w-px bg-simuflex-line" />

            <button
              type="button"
              onClick={logout}
              className="flex items-center space-x-1.5 text-xs font-bold text-simuflex-orange hover:text-orange-600 transition-colors uppercase tracking-wider"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>Exit</span>
            </button>
          </div>
        </header>

        {/* Content canvas */}
        <main className="flex-1 overflow-y-auto px-8 py-8 scrollbar-thin animate-fade-in bg-simuflex-mist/50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
